import type { AdminWalletTransaction } from "@shopexperts/legacy-api";

export type HoldDebitTransaction = Pick<
  AdminWalletTransaction,
  "id" | "remaining_hold_cents" | "currency"
>;

export interface HoldDebitAllocation<T extends HoldDebitTransaction = HoldDebitTransaction> {
  transaction: T;
  releasableCents: number;
  debitedCents: number;
  remainingCents: number;
  status: "fully_debited" | "partially_debited" | "unused";
}

export function preserveHoldSelectionOrder(
  currentIds: readonly string[],
  nextIds: readonly string[],
): string[] {
  const nextIdSet = new Set(nextIds);
  const retainedIds = currentIds.filter((id) => nextIdSet.has(id));
  const addedIds = nextIds.filter((id) => !currentIds.includes(id));
  return [...retainedIds, ...addedIds];
}

export function buildHoldDebitAllocations<T extends HoldDebitTransaction>(
  transactions: readonly T[],
  selectedIds: readonly (number | string)[],
  requestedCents: number,
): HoldDebitAllocation<T>[] {
  const transactionsById = new Map(transactions.map((transaction) => [transaction.id, transaction]));
  const seenIds = new Set<number>();
  let centsLeftToAllocate = Number.isFinite(requestedCents)
    ? Math.max(0, Math.trunc(requestedCents))
    : 0;

  return selectedIds.flatMap((selectedId) => {
    const id = Number(selectedId);
    const transaction = transactionsById.get(id);
    if (!transaction || seenIds.has(id)) return [];

    seenIds.add(id);
    const releasableCents = Math.max(0, transaction.remaining_hold_cents ?? 0);
    const debitedCents = Math.min(releasableCents, centsLeftToAllocate);
    const remainingCents = releasableCents - debitedCents;
    centsLeftToAllocate -= debitedCents;

    return [{
      transaction,
      releasableCents,
      debitedCents,
      remainingCents,
      status:
        debitedCents === 0
          ? "unused"
          : remainingCents === 0
            ? "fully_debited"
            : "partially_debited",
    }];
  });
}
